import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react'

import {
  computeAutoTextareaHeight,
  maxTextareaViewportPx,
  measureTextareaContentHeight,
  textareaOverflowY,
} from './autoGrowTextarea'

export function useVerticalTextareaResize(
  text: string,
  enabled: boolean
): {
  textareaRef: RefObject<HTMLTextAreaElement | null>
  syncHeight: () => void
  onResizePointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void
} {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [manualHeightPx, setManualHeightPx] = useState<number | null>(null)
  const [maxPx, setMaxPx] = useState(maxTextareaViewportPx)
  const dragRef = useRef(false)

  const syncHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    const content = measureTextareaContentHeight(el)
    const next = computeAutoTextareaHeight(content, manualHeightPx, maxPx)
    el.style.height = `${next}px`
    el.style.overflowY = textareaOverflowY(content, next)
  }, [manualHeightPx, maxPx])

  useEffect(() => {
    if (!enabled) {
      setManualHeightPx(null)
      return
    }
    setMaxPx(maxTextareaViewportPx())
    const onWindowResize = () => setMaxPx(maxTextareaViewportPx())
    window.addEventListener('resize', onWindowResize)
    return () => window.removeEventListener('resize', onWindowResize)
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    syncHeight()
  }, [text, enabled, syncHeight, maxPx])

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const el = textareaRef.current
      if (!el) return

      dragRef.current = true
      const startY = event.clientY
      const startHeight = el.getBoundingClientRect().height

      const onMove = (moveEvent: globalThis.PointerEvent) => {
        if (!dragRef.current) return
        const delta = moveEvent.clientY - startY
        const content = measureTextareaContentHeight(el)
        const next = computeAutoTextareaHeight(
          content,
          startHeight + delta,
          maxPx
        )
        setManualHeightPx(next)
        el.style.height = `${next}px`
        el.style.overflowY = textareaOverflowY(content, next)
      }

      const onUp = () => {
        dragRef.current = false
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    [maxPx]
  )

  return { textareaRef, syncHeight, onResizePointerDown }
}
